export const markdownGSD = `

# Data & Design, AI for Urban Data and Visualization

### Data, Design Computation, Artificial Intelligence, Visualization, Harvard GSD J Term 2023


**KEYWORD:**

Vector, Raster, Urban Data, GIS, Data Processing, Data Mining, Machine Learning, Artificial Intelligence, Visualization, Mapping, Design Decision-Making ...

**SHORT DESCRIPTION:**

This course contains several introductory lectures and hands-on workshops for those who want to use data as design materials to develop the design process. 

We will gently visit the basic concepts and implementations of the topics: Code, Data and processing, Geometry data, Vector, Raster, and some machine learning models and their related technologies, such examples: Regression, Classification, Pattern, Data Representation, Dimensionality Reduction, Machine Learning, Deep Learning, Implementation, Mapping, and Visualization Methodologies. 

From the designer's perspective, students will better understand and implement the use of data and tools, finally, gain holistic high-level concepts to expand that knowledge and technology further. Therefore, the ideas and contents you will learn in this course could become a map for those who want to learn how to use data and digital media in design.

Each day, students will learn the individual topics listed below. Then, students will make a group to discuss, help, understand, and finish homework and examples. All code and examples will be online, and the instructor will be available before and after class for troubleshooting.

In addition, it is also possible for an individual or group to focus on one of the primary topics and revisit other topics after the course based on the student's ability and expectations. It is yours if you are interested in data as a design material.

**DELIVERABLES**:

Class materials and homework

**LOCATION:** 

TBA

**DATE:** 

TUE(03), WED(04), THU(05), FRI(06), Mon(9), January 2023

**TIME:** 

3PM ~ 5PM(EST)

20% for lectures, 70% for workshops, and 10% for exercise and troubleshooting


**TOOL**

Programming Language: [Python](https://www.python.org/)([Anaconda](https://docs.anaconda.com/anaconda/install/index.html)), [Typescript](https://www.typescriptlang.org/)([Javascript](https://developer.mozilla.org/en-US/docs/Web/JavaScript))

Library: [NJSCore](https://www.npmjs.com/package/njscore), [Numpy](https://numpy.org/), [Pandas](https://pandas.pydata.org/), [Tensorflow](https://www.tensorflow.org/), [TensorflowJS](https://www.tensorflow.org/js)

Software: [Visual Studio Code](https://code.visualstudio.com/Download), [NodeJS](https://nodejs.org/en/), [Github Desktop](https://desktop.github.com/)


**PARTICIPANTS**

Up to 10 students


**Prerequisite**

Experience with one of modern programming languages(Python, Java, Javascript, Typescript, C, C++, C#, or Swift)


-----

#### 1. Data & Design: Code for Design


![Data in Design](https://raw.githubusercontent.com/NamjuLee/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/main/public/reference/img/dataDesignSequence.jpg)


**LECTURE** - [Data in Design](https://medium.com/@nj-namju/data-design-c21457dc8dc)

The introduction to the key topic: Data in Design, helps you understand the meaning of using data in the design process through several examples. 

---------------------------------

#### 2. Programming and data processing

![](https://raw.githubusercontent.com/NamjuLee/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/main/public/reference/img/programming.jpg)

**WORKSHOP**

Data processing, Data Type, Memory, Python, Numpy, Pandas library, ...

As a first step, We will learn the basic concept of programming, dealing with logical flows and data manipulation.


LAB 01 - Python Basic, Condition & Loop, Primitive Data Structure 

LAB 02 - File format, Data Structure, object(class)

LAB 04 - Matrix, Numpy basic

LAB 05 - Pandas basic

---------------------------------

#### 3. Vector and Raster as design data

![](https://raw.githubusercontent.com/NamjuLee/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/main/public/reference/img/vectorRaster-1.jpg)

**LECTURE** 

Understanding the tool for qualification and quantification is crucial for using data as design material. We will learn basic design and how to decompose design elements as vector and raster data. In addition, it can allow us to think and set up the design process.

---------------------------------

#### 4. Geometry Data

![](https://raw.githubusercontent.com/NamjuLee/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/main/public/reference/img/vector.jpg)

**WORKSHOP**

LAB 01 - Vector Data, Vector Point, 

LAB 02 - Line, Polyline, Polygon, 

LAB 03 - Mesh, 

LAB 04 - Raster Data, Image, Matrix, 

LAB 05 - Color, Pixel, Voxel

LAB 06 - Web implementation: [Point](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/geometry-implementation/canvas-point-circle), [Line](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/geometry-implementation/canvas-line-polyline),[Interactive Polyline](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/geometry-implementation/canvas-polyline-mouse-input), [Polygon](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/geometry-implementation/canvas-polygon), [Mesh](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/geometry-implementation/three-mesh), [Geometry as data structure](http://localhost:3000/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/geometry-implementation/geometry-as-data-structure), [Pixel map](http://localhost:3000/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/geometry-implementation/pixel), [Graph](http://localhost:3000/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/geometry-implementation/graph) and [Voxel map](http://localhost:3000/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/geometry-implementation/voxel)

---------------------------------

#### 5. Introduction to AI models and Implementations

![](https://raw.githubusercontent.com/NamjuLee/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/main/public/reference/img/ml.jpg)

**LECTURE**

Regression, Classification, Machine Learning, Deep Learning

We will gain a high-level understanding of AI in design: Supervised and Unsupervised Learning and related models. Students will go through several examples, enabling us to expand the use of models in design processes.

#### 6. Problem, Data(Vector & Raster), Model, Train, Validation

**WORKSHOP**

![](https://raw.githubusercontent.com/NamjuLee/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/main/public/reference/img/digitalTexture.jpg)

Tensorflow, Keras, Numpy, Pandas, and other libraries

LAB 01 - Temperature conversion

LAB 02 - Multiplication table 

LAB 03 - Smart Drawing 

LAB 04 - Digital Texture prediction

LAB 05 - Map Classifier

LAB 06 - Data Reference(Vector & Raster) & Basic Models(Regression & Classification: Linear Regression, Ridge, Lasso, ElasticNet, Naive Bayes, Polynomial Regression, KNN, Logistic Regression, Decision Tree Classifier, SVM, ANN, CNN)

LAB 07 - Examples - Map classification, GAN ...

LAB 08 - Web implementation(TensorflowJS: [Regression](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/ml-models/polynomial-regression), [Classification](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/ml-models/tfjs-iris), [SmartDrawing](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/ml-models/smart-drawing), [t-SNE](https://namjulee.github.io/njs-lab-public/lab/machine-learning/storymaps-tsne-viz), [Image](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/ml-models/image-face), [Video](https://namjulee.github.io/njs-lab-public/lab/machine-learning/tf-video-face))

---------------------------------

#### 7. Introduction to Third Place Prediction Research

![](https://raw.githubusercontent.com/NamjuLee/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/main/public/reference/img/thirdPlace.jpg)

**LECTURE** : [Reading](https://nj-namju.medium.com/third-place-analysis-and-implementation-design-data-artificial-intelligence-bf802a8e7e0a)

We will take look the Third Place research([Initial work](http://www.njstudio.co.kr/main/project/2016_MobilityEnergyConsumptionMITMediaLab/index.html), [Paper](https://link.springer.com/chapter/10.1007/978-981-33-4400-6_11), [AI Model](https://lnkd.in/gdf6d8j7)), 


Network Analysis, Dimension Reduction, Cost function, Decay function ... 

LAB 01 - Parsing data, Google Place API

LAB 02 - Parsing, Processing, Visualizing Data

LAB 03 - Processing Data For Train

LAB 04 - Training Models

LAB 05 - Network: Distance(Euclidean) and Decay model 

LAB 06 - Fitting Network and Implementation

LAB 07 - Networks for Boston, LA, and Redlands

---------------------------------

#### 8. Third Place Prediction model and Implementation

![](https://raw.githubusercontent.com/NamjuLee/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/main/public/reference/img/thirdPlaceImple.jpg)

**WORKSHOP**

LAB 01 - [Visualization](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/third-place-viz/third-place-init), 

LAB 02 - [Model implementation](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/third-place-viz/third-place-result)(Boston, LA, and Redlands)

---------------------------------

#### 9. Introduction to Data Visualization and Digital Mapping

![](https://raw.githubusercontent.com/NamjuLee/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/main/public/reference/img/mapping.jpg)

**LECTURE**

Principles of Graphical Integrity, Visualization, Projection, Generalization

Pipeline for visualization, Mapping, Methodology, and Implementation

Implementing digital mapping and visualization on the web environment, Understanding boilerplate code and the pipeline

LAB 01 - [Typescript Basic](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/typescript-lab)

LAB 02 - Boilerplate([2D](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/digital-mapping-lab/template), [3D](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/3d-viz-for-gis/three-starter)) code and the pipeline

---------------------------------


#### 10. Interactive Visualization on Web

![](https://raw.githubusercontent.com/NamjuLee/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/main/public/reference/img/mapping2.jpg)

**WORKSHOP**

This part is about implementing interactive visualization with urban data and the results from Machine Learning on a web browser. We will examine practical digital mapping techniques such as Bin, Color Blending, and more.


ArcGIS JSAPI / MapboxGL / HTML Canvas / njsCore / Typescript ...
Point, Line, Polygon, Interaction, Bin, Color Computation Blending mode using HTML Canvas 


LAB 01 - [Basic visualization](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/digital-mapping-lab/json-line)

LAB 02 - Visualization Vector and Raster

LAB 03 - Visualization analysis methods([Bin](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/digital-mapping-lab/dynamic-bins)) and tools([Graph](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/digital-mapping-lab/a-start-grid), [Network Analysis](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/digital-mapping-lab/network-search(bfs)))

---------------------------------


#### 11. 3D Visualization for GIS on Web

We will learn how to visualize data: vector(Point, Line Polyline Polygon or Mesh) and raster(Image), as a form of geometries with colors on web environment.

![](https://raw.githubusercontent.com/NamjuLee/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/main/public/reference/img/3DVizForGIS.jpg)

**WORKSHOP**

LAB 01 - [THREE JS basic](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/3d-viz-for-gis/three-primitive) and the [pipeline](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/3d-viz-for-gis/three-starter)

LAB 02 - [Point](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/3d-viz-for-gis/three-point), [Line, Polyline](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/3d-viz-for-gis/three-line-polyline), [Mesh]((https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/3d-viz-for-gis/three-mesh-terrain)) [(Rhino Grasshopper)](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/3d-viz-for-gis/three-mesh-rhino-geo)

LAB 03 - [Slowzone project review](https://namjulee.github.io/Data-Design-AI-for-Urban-Data-and-Viz-Harvard-GSD-public/lab/3d-viz-for-gis/slow-zone-viz-3d)


---

! This schedule could be revised based on the student's expectations and time.

---


**Reference:**

Paper: https://www.springer.com/gp/book/9789813343993
                https://link.springer.com/chapter/10.1007/978-981-33-4400-6_11

Medium: https://medium.com/@nj-namju/third-place-analysis-and-implementation-design-data-artificial-intelligence-bf802a8e7e0a

GitHub: https://github.com/NamjuLee/Third-Place-Prediction-Report-V2022

Introduction to Computational Design: Data, Geometry, and Visualization Using Digital Media: https://nj-namju.medium.com/introduction-to-computational-design-data-geometry-and-visualization-using-digital-media-14161fdfd22f

Computational Design Thinking for Designers: https://nj-namju.medium.com/computational-design-thinking-for-designers-68224bb07f5c

`;